// library imports
import { LatLng, LatLngBounds, LeafletEvent, Map as LeafletMap, Point } from 'leaflet'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AttributionControl,
  MapContainer,
  TileLayer,
  useMap,
  useMapEvents
} from 'react-leaflet'

// const
import { CLASS_PREFIX } from 'src/constants/'
import * as MAP from 'src/constants/map'

// interfaces
import { IMapStyleToggle } from 'src/interfaces/'
import { IBoundingBox, IPosition } from 'src/interfaces/location'

import { IMapComponent } from 'src/interfaces/map'

// components
import GeoJsonLayerCollection from 'src/components/map/geojson_layer_collection/geojson_layer_collection'
import MapMarkerCollection from 'src/components/map/marker_collection/map_marker_collection'
import MapShape from 'src/components/map/shape/map_shape'
import { MAP_CONTROLS_TOP_GROUP_CLASS } from 'src/components/map/map_controls/map_controls'
import MapControlsContainer from 'src/containers/map_controls'

// helpers
import {
  getBoundingBoxFromLeafletBounds,
  hasMapMovedEnough
} from 'src/utils/map'
import { reportError } from 'src/utils/reporting/report-errors'
import { getToggle } from 'src/utils/toggle'
import { boundsOfAll } from 'src/utils/map/geojson_bounds'
import { boundsAround, clearanceFor, coverageAtTop, fitCenterOf } from 'src/utils/map/view_limits'
import { IZoomRange, IZoomRangeToggle, zoomRangeOf, zoomRangeOfToggle } from 'src/utils/map/zoom_range'

import { useFeatureSettings } from '@lopo-tech/frontends-shared'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { AppDispatch, setBoundingBox as setBoundingBoxAction } from 'src/actions/app-state/app-state'
import 'src/components/map/map.scss'
import { useGetBoundingBox } from 'src/reducers/app-state/app-state_hooks'
import { getConfigValue } from 'src/utils/config/config'

const cls = CLASS_PREFIX + 'map'

const defaultMapStyle: IMapStyleToggle = {
  styleURL: MAP.MAP_DEFAULT_MAPBOX_STYLE
}

const mapStyleToggle = getToggle<IMapStyleToggle>('mapStyle', defaultMapStyle)

// The range the map falls back to when neither the settings nor the toggle carry one. Typed like
// the other two sources.
const startingZoom: IZoomRange = {
  default: MAP.MAP_DEFAULT_ZOOM,
  max: MAP.MAP_OPTION_MAX_ZOOM,
  min: MAP.MAP_OPTION_MIN_ZOOM
}

// The `mapZoom` toggle, still read as a fallback while instances move their values into the
// configuration. Read once at module level, as it always was: it comes from `/js_env`, which is
// there before anything renders.
const toggleRange = zoomRangeOfToggle(getToggle<IZoomRangeToggle | undefined>('mapZoom', undefined))

const rangeKey = (range: IZoomRange): string => `${range.min}/${range.max}/${range.default}`

/**
 * How far down the map the group of controls floating over its top reaches.
 *
 * That group takes pointer events across the whole width, so a project drawn underneath it
 * cannot be opened, and the fit is what decides whether one ends up there. Read from the element
 * rather than written down here, so that a portal whose controls are a different size gets the
 * room it needs.
 *
 * A group that holds nothing reaches nowhere, however far down its own box sits: it is laid out a
 * margin below the top edge whether or not there is a button in it, and `coverageAtTop` is what
 * keeps that margin from being kept free for nothing.
 */
const coveredAtTop = (map: LeafletMap): number => {
  const container = map.getContainer()
  const group = container.querySelector(`.${MAP_CONTROLS_TOP_GROUP_CLASS}`)
  if (!group) {
    return 0
  }

  return coverageAtTop(
    group.getBoundingClientRect(),
    container.getBoundingClientRect().top
  )
}

/**
 * The view the map opens on, and the zoom range it opens inside.
 *
 * One component for both, because both set the same thing. Two, each with its own idea of whether
 * the visitor has moved, lose to each other: a `setZoom` under one component's own flag is an
 * ordinary `zoomend` to the other, and the range arrives before the projects do — the second
 * component would have read the first as the visitor and never fitted at all.
 *
 * `MapContainer` reads `zoom`, `minZoom` and `maxZoom` only once, while it builds the map; later
 * props do nothing, and the zoom a box fits at is not known before the map exists, because it
 * depends on the container. Both jobs therefore happen from inside the map.
 *
 * What it does, whenever the range changes or the map is newly armed, the visitor has not touched
 * it and no pan is running: fit to the box if there is one, otherwise set the range's own zoom,
 * and in either case hand the range's bounds to the map.
 *
 * Nine things in here are load-bearing, and each of them is a way the starting view has been
 * seen to go wrong.
 *
 * 1. The bounds are widened before the view is set and narrowed afterwards. Setting them straight
 *    away clamps a zoom that lies outside, and a clamp within four levels **animates**: it is
 *    deferred to the next frame and undoes the zoom that was set in between. A range of
 *    14 to 18 starting at 16 ended up at 14.
 * 2. Whether the visitor moved is read before anything is touched, because a clamp fires
 *    `zoomend` and would look exactly like them having moved.
 * 3. The zoom is set only when it differs, and `onZoomed` runs only once the map really is on
 *    the new level. Leaflet drops the zoom as well as the clamp while a zoom animation of the
 *    visitor's is running, and a caller that requeries over a zoom that never happened would
 *    take a section the visitor never asked for.
 * 4. Nothing is applied while the pan `panTo` started is running. `setZoom` calls `_stop()`,
 *    which freezes that pan where it is, and nothing starts it again: the map stayed short of
 *    the town it was meant to show, and the feed queried that section.
 * 5. **The bounds of a range are handed on even when nothing about the view changes.** A range
 *    that differs only in its ends, or one that arrives after the visitor has taken hold of the
 *    map, changes no zoom and fits nothing, and a map that never hears its ends would let the
 *    visitor out of them.
 * 6. **The zoom of a fit is worked out and clamped here, not left to `fitBounds`.**
 *    `getBoundsZoom` clamps to the minimum *currently* set, which during step 1 is the widened
 *    one. A box needing a level below a new, higher minimum would land there, and narrowing the
 *    bounds afterwards would clamp it — animated, a frame later, outside the window of the flag
 *    below. Clamped to the range instead, so there is nothing left to clamp. The upper end is the
 *    range's own zoom: a fit never goes closer than the map would have opened anyway.
 * 7. **The size is refreshed first.** A container can change its height after the map inside it
 *    was built, and Leaflet is not told: every page of this layout builds its own map, and it
 *    does so before that page's column has settled. Without this the fit is worked out for a
 *    container that was never there.
 * 8. **What is on screen is handed on before the view changes.** The section the feeds ask for is
 *    picked up from whatever the map shows once a pan has ended, and that runs after this. Handed
 *    on here first, it stays the place the visitor came for rather than the box of its projects.
 * 9. **A range that arrives late fits again**, rather than being skipped because a fit has
 *    already happened; that is what `rangeChanged` is doing inside `fitting`. Without it a fit
 *    clamped by the old minimum would stay clamped after a lower one arrives, with projects cut
 *    off that now would fit.
 *
 * The flag that marks the map's own changes lives in `MapComponent`, because the search-this-area
 * button has to know about them too.
 */
const InitialView: React.FC<{
  range: IZoomRange;
  builtWith: IZoomRange;
  withBounds: boolean;
  panning: boolean;
  bounds?: LatLngBounds;
  // One value per arming, not per location: coming back to a location the map has already fitted
  // is a new starting view, while layers arriving one page at a time is not.
  arming?: string;
  // The location itself, which decides only whether what the visitor did still counts.
  locationKey?: string;
  // Whether the route this map is on asked for the view to be left alone. A location reached that
  // way is not a new starting view, however far it is from the one before.
  keepView: boolean;
  ownChange: React.MutableRefObject<boolean>;
  onHoldArea: (zoom: number) => void;
  onFitted: (view: LatLngBounds) => void;
  onZoomed: () => void;
}> = ({
  range,
  builtWith,
  withBounds,
  panning,
  bounds,
  arming,
  locationKey,
  keepView,
  ownChange,
  onHoldArea,
  onFitted,
  onZoomed
}) => {
  const map = useMap()
  // The values last applied, not a flag: a range can be replaced, and the map starts out with one
  // already applied to it.
  const applied = useRef(rangeKey(builtWith))
  // The arming the box was last fitted for.
  const fittedFor = useRef<string | undefined>(undefined)
  const lastLocation = useRef<string | undefined>(locationKey)
  const moved = useRef(false)

  // These have to watch from the first render on, not from the moment a range arrives: mounted
  // any later they never see the visitor move, and the start zoom pulls them back — someone at
  // zoom 16 landed on 14. `dragstart` is there for the drag that is already under way when the
  // projects arrive, which would otherwise only be heard once it ends.
  useMapEvents({
    dragstart: () => { if (!ownChange.current) { moved.current = true } },
    dragend: () => { if (!ownChange.current) { moved.current = true } },
    zoomend: () => { if (!ownChange.current) { moved.current = true } }
  })

  useEffect(() => {
    // A different location is a different starting view, so what the visitor did to the one
    // before is spent. Not where they were carried there by the search-this-area button: that
    // exists to keep the section they just chose, and fitting over it would be exactly wrong.
    if (lastLocation.current !== locationKey) {
      lastLocation.current = locationKey
      if (!keepView) {
        moved.current = false
      }
    }

    const wanted = rangeKey(range)
    const rangeChanged = applied.current !== wanted
    const fitting = !!bounds && arming !== undefined && !moved.current &&
      (fittedFor.current !== arming || rangeChanged)

    if ((!rangeChanged && !fitting) || panning) {
      return
    }
    // A later range may still set the start zoom, as long as the visitor has not touched the map
    // and there is no box that says better. The range the map was built with is usually not the
    // last one: the toggle and the build snapshot are there at once, the settings later.
    const zooming = !fitting && !moved.current && map.getZoom() !== range.default

    applied.current = wanted

    if (fitting) {
      map.invalidateSize({ pan: false })
    }
    if (fitting || zooming) {
      onHoldArea(range.default)
    }

    ownChange.current = true
    if (withBounds) {
      // Widen first: a clamp out of `setMinZoom`/`setMaxZoom` animates when it is within four
      // levels, lands in the next frame and undoes the zoom set in between.
      map.setMinZoom(Math.min(range.min, map.getMinZoom()))
      map.setMaxZoom(Math.max(range.max, map.getMaxZoom()))
    }

    if (fitting && bounds) {
      const even = MAP.MAP_FIT_PADDING
      // The level is worked out with the same margin on every side. Taking the controls off the
      // top here too would cost a level the map often cannot give back, because the minimum of
      // the instance clamps it straight away — and then the box would be pushed out of the top
      // rather than clear of the controls.
      const zoom = Math.max(range.min, Math.min(
        range.default,
        map.getBoundsZoom(bounds, false, new Point(2 * even, 2 * even))
      ))
      const clearing = clearanceFor(
        bounds, map.getSize(), zoom, even, coveredAtTop(map), map.options.crs
      )
      const centre = fitCenterOf(
        bounds,
        zoom,
        new Point(even, even + clearing),
        new Point(even, even),
        map.options.crs
      )
      map.setView(centre, zoom, { animate: false })
      fittedFor.current = arming
    } else if (zooming) {
      map.setZoom(range.default, { animate: false })
    }

    if (withBounds) {
      map.setMinZoom(range.min)
      map.setMaxZoom(range.max)
    }
    ownChange.current = false

    if (fitting) {
      onFitted(map.getBounds())
    } else if (zooming && map.getZoom() === range.default) {
      onZoomed()
    }
  }, [map, range, withBounds, panning, bounds, arming, locationKey, keepView, ownChange, onHoldArea, onFitted, onZoomed])

  return null
}

const EventHandler = ({ eventHandler, moveend }: { eventHandler: (event: LeafletEvent) => void, moveend: (event: LeafletEvent) => void }) => {
  useMapEvents({
    dragend: eventHandler,
    zoomend: eventHandler,
    moveend
  })

  return null
}

const MapComponent: React.FC<IMapComponent> = ({
  isLocked,
  position,
  shape,
  markers,
  mapToken,
  showLocationShapeControls = true,
  geoJsonLayers,
  locationKey
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const locationState = useLocation<{ keepBoundingBox?: boolean }>().state
  // Settings first, then the toggle, then the constants. All or nothing holds per source: an
  // incomplete set of settings falls back to the toggle, not past it to the constants.
  const zoomRange = zoomRangeOf(useFeatureSettings()) ?? toggleRange ?? startingZoom
  // What the map is built with, held from the first render on. Whatever else the map is given
  // later, this stays what it was built with, and `InitialView` needs to know it.
  const builtWith = useRef(zoomRange)
  // the map ref is only ready at the time of the next render AFTER the whenReady event,
  // so we need to trigger a rerender to ensure we can calculate the bounding box correctly
  const [, setIsMapReady] = useState(false)

  // we want to recalculate the bounding box only after panning has completed, when we select a shape via the location search input
  const [panning, setPanning] = useState(false)

  const mapRef = useRef<LeafletMap | null>(null)

  const boundingBox = useGetBoundingBox()
  // The section as it stands right now, including a write made in this same round. Two things
  // here run inside one commit and have to see each other: `InitialView` holds on to the section
  // from an effect of its own, and the recalculation below runs afterwards — the effects of a
  // child run before those of its parent, so the parent's own value is still the one from the
  // render before, and it would write over what the child just kept.
  const boundingBoxRef = useRef(boundingBox)
  boundingBoxRef.current = boundingBox
  const setBoundingBox = useCallback(
    (bb: IBoundingBox | undefined) => {
      boundingBoxRef.current = bb
      dispatch(setBoundingBoxAction(bb))
    },
    [dispatch]
  )

  // Set while this component or the one setting the initial view touches the map, so that their
  // own events do not read as the visitor. Both the "moved" flag inside `InitialView` and the
  // search-this-area button below depend on it, which is why it lives up here.
  const ownChange = useRef(false)
  // What the search-this-area button measures against while the map itself set the view. Empty
  // means "whatever the feeds are asking for", which is what it was before any fit.
  const viewBaseline = useRef<IBoundingBox | undefined>(undefined)
  // Whether the section the feeds are asking for is the one this map wrote for its own starting
  // view. One the visitor chose with the button is not, and nothing here may overwrite it.
  const areaIsOurs = useRef(false)

  const [showSearchAreaButton, setShowSearchAreaButton] = useState(false)

  // After a zoom the visitor did not ask for: the bounding box is still the one of the level the
  // map was built with, and `handleMapChange` has turned the difference into a "search this area"
  // button. Reset both, so that the next query takes the section that is on screen.
  const forgetBoundingBox = useCallback(() => {
    setBoundingBox(undefined)
    viewBaseline.current = undefined
    areaIsOurs.current = false
    setShowSearchAreaButton(false)
  }, [setBoundingBox])

  // The section the feeds are asking for, held on to before the starting view changes it. Without
  // it the recalculation below would pick up the fitted box, and every feed the visitor opens
  // next would ask for the section of some projects instead of the place they came for.
  //
  // It is the place around the map's own centre at the zoom the range opens on, which is what
  // this map showed before it fitted anything. Stated that way rather than read off the map, so
  // that a range arriving after the fit restates it on its own zoom instead of handing on
  // whatever the projects happen to cover.
  //
  // A section the visitor chose with the search-this-area button is not this map's to restate,
  // and `ours` is what tells the two apart.
  const holdArea = useCallback((zoom: number): void => {
    const map = mapRef.current
    if (!map) {
      return
    }
    const held = boundingBoxRef.current
    if (held && !areaIsOurs.current) {
      return
    }
    const centre = held
      ? fitCenterOf(
        new LatLngBounds(
          [held.southWest.latitude, held.southWest.longitude],
          [held.northEast.latitude, held.northEast.longitude]
        ),
        zoom,
        undefined,
        undefined,
        map.options.crs
      )
      : map.getCenter()
    areaIsOurs.current = true
    setBoundingBox(getBoundingBoxFromLeafletBounds(
      boundsAround(centre, map.getSize(), zoom, 0, map.options.crs)
    ))
  }, [setBoundingBox])

  // What the map opened on once it was fitted. From here on the button measures the visitor's
  // movement against that view rather than against the feeds' section, which the fit left alone.
  const rememberFittedView = useCallback((view: LatLngBounds): void => {
    viewBaseline.current = getBoundingBoxFromLeafletBounds(view)
  }, [])

  const handleMapChange = useCallback(
    ({ target: map }: LeafletEvent): void => {
      // The map moving because this component moved it is not the visitor moving it. A view set
      // without animation fires its events inside the call, so the flag covers exactly that.
      if (ownChange.current) {
        return
      }

      const newMapBoundingBox = getBoundingBoxFromLeafletBounds(
        map.getBounds()
      )
      const newShowSearchAreaButton = getConfigValue(
        'featureFlags.location.defaultAddress'
      )
        ? false
        : hasMapMovedEnough(viewBaseline.current ?? boundingBox, newMapBoundingBox)

      setShowSearchAreaButton(newShowSearchAreaButton)
    },
    [boundingBox]
  )

  // update bounding box when user clicks the "search this area" button
  const updateBoundingBox = useCallback((): void => {
    if (mapRef.current) {
      setBoundingBox(
        getBoundingBoxFromLeafletBounds(mapRef.current.getBounds())
      )
      // The section the visitor just chose is the one to measure against from now on; leaving the
      // fitted view in place would put the button back after the smallest move. It is theirs, so
      // nothing here restates it either.
      viewBaseline.current = undefined
      areaIsOurs.current = false
      setShowSearchAreaButton(false)
    }
  }, [setBoundingBox])

  const panTo = useCallback((targetPosition: IPosition) => {
    if (!mapRef.current) {
      return
    }
    setPanning(true)
    setBoundingBox(undefined)
    viewBaseline.current = undefined
    areaIsOurs.current = false
    const panOptions = {
      animate: true,
      duration: 0.5
    }
    const offsetLat =
      parseFloat(targetPosition.latitude.toString()) - MAP.MAP_LAT_OFFSET
    mapRef.current.panTo([offsetLat, targetPosition.longitude], panOptions)
  }, [])

  const backToPrevious = useCallback((): void => {
    if (position) {
      panTo(position)
      setShowSearchAreaButton(false)
    }
  }, [position, panTo])

  const checkToRecalculateBoundingBox = useCallback(() => {
    if (mapRef.current && !boundingBoxRef.current && !panning) {
      const bounds = mapRef.current.getBounds()
      const bb = getBoundingBoxFromLeafletBounds(bounds)
      areaIsOurs.current = true
      setBoundingBox(bb)
    }
  }, [setBoundingBox, panning])

  useEffect(() => {
    checkToRecalculateBoundingBox()
  })

  const initialPosition = position || MAP.CENTER_OF_GERMANY_POSITION
  const center = new LatLng(
    initialPosition.latitude,
    initialPosition.longitude
  )

  const tileLayerUrl =
    `https://{s}.tiles.mapbox.com/styles/v1/${mapStyleToggle.styleURL}` +
    `/tiles/{z}/{x}/{y}?access_token=${mapToken}`

  if (!position) {
    reportError('map called without position', position)
  }

  // Panning is what it was: only a position that really changed moves the map. Anything else in
  // here would pan on a render that changed nothing about where the map belongs — on the way to
  // another location the route changes before the location does, and the map would set off
  // towards the place it is leaving.
  useEffect(() => {
    if (position && !locationState?.keepBoundingBox) {
      panTo(position)
    }
  }, [position, panTo])

  // Whether the starting view is still this map's to set, and for which turn. Its own effect,
  // and it never moves the map.
  //
  // One value per arming rather than per location: arriving at a location the map has fitted
  // before is a new starting view, and the map has to be able to tell that from the same
  // location's layers arriving one page at a time.
  //
  // It is worked out here rather than inside the map, because the effect of a parent runs after
  // those of its children: in the render that brings a new location the child still sees the
  // arming of the one before and waits, and by the next render a pan is under way for it to wait
  // on. Without that it would fit in that same render — the layers of a location already in the
  // cache are drawn before the pan starts — and `panTo` would pull the view off the projects
  // again right afterwards.
  const armings = useRef(0)
  const [arming, setArming] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (locationKey === undefined || locationState?.keepBoundingBox) {
      setArming(undefined)
      return
    }
    armings.current += 1
    setArming(`${locationKey}/${armings.current}`)
  }, [locationKey, locationState?.keepBoundingBox])

  // The section the listed projects cover, by the rule the backend builds a project's bounding box
  // with. Only where this map is showing the location it is armed for, so that a deep link cannot
  // fit to the projects of the location still in local storage.
  //
  // Keyed on what the documents are rather than on the array, which is a new one on every render
  // of the page above.
  const layerKey = (geoJsonLayers ?? []).map((layer) => layer.id).join(',')
  const projectBounds = useMemo(
    () => (arming !== undefined && arming.startsWith(`${locationKey}/`)
      ? boundsOfAll((geoJsonLayers ?? []).map((layer) => layer.geojson))
      : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layerKey, arming, locationKey]
  )

  return (
    <div className={cls}>
      <MapContainer
        attributionControl={MAP.MAP_OPTION_ATTRIBUTION_CONTROL}
        boxZoom={!isLocked}
        className={cls + '__container'}
        center={center}
        doubleClickZoom={!isLocked}
        dragging={!isLocked}
        keyboard={!isLocked}
        maxBounds={MAP.MAP_OPTION_MAX_BOUNDS}
        maxZoom={isLocked ? undefined : builtWith.current.max}
        minZoom={isLocked ? undefined : builtWith.current.min}
        ref={mapRef}
        scrollWheelZoom={!isLocked}
        touchZoom={!isLocked}
        zoomControl={!isLocked && MAP.MAP_OPTION_ZOOM_CONTROL}
        whenReady={() => setIsMapReady(true)}
        zoom={builtWith.current.default}
      >
        <EventHandler eventHandler={handleMapChange} moveend={() => setPanning(false)} />
        <InitialView
          range={zoomRange}
          builtWith={builtWith.current}
          withBounds={!isLocked}
          panning={panning}
          bounds={projectBounds}
          arming={arming !== undefined && arming.startsWith(`${locationKey}/`) ? arming : undefined}
          locationKey={locationKey}
          keepView={!!locationState?.keepBoundingBox}
          ownChange={ownChange}
          onHoldArea={holdArea}
          onFitted={rememberFittedView}
          onZoomed={forgetBoundingBox}
        />
        <AttributionControl position={'topright'} prefix="" />
        <TileLayer
          url={tileLayerUrl}
          attribution={MAP.MAP_ATTRIBUTION}
          maxZoom={MAP.MAP_OPTION_TILE_MAX_ZOOM}
          tileSize={MAP.MAP_OPTION_TILE_SIZE}
          zoomOffset={MAP.MAP_OPTION_ZOOM_OFFSET}
        />

        <MapMarkerCollection markers={markers} />
        {shape && <MapShape {...shape} />}
        {!!geoJsonLayers?.length && <GeoJsonLayerCollection layers={geoJsonLayers} />}
        <MapControlsContainer
          isLocked={isLocked}
          showSearchAreaButton={showSearchAreaButton}
          updateBoundingBox={updateBoundingBox}
          mapRef={mapRef}
          backToPrevious={backToPrevious}
          showLocationShapeControls={showLocationShapeControls}
        />
      </MapContainer>
    </div>
  )
}

export default MapComponent
